import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    LayoutDashboard, 
    FolderKanban, 
    ListTodo, 
    Users, 
    Clock, 
    FileText,
    Menu,
    X,
    LogOut,
    User
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Projects', href: '/projects', icon: FolderKanban },
    { name: 'Tasks', href: '/tasks', icon: ListTodo },
    { name: 'Team', href: '/team', icon: Users },
    { name: 'Time Entries', href: '/time-entries', icon: Clock },
    { name: 'Reports', href: '/reports', icon: FileText },
];

export const Layout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const NavLinks = ({ onItemClick }) => (
        <nav className="space-y-1">
            {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;
                return (
                    <Link
                        key={item.name}
                        to={item.href}
                        onClick={onItemClick}
                        className={`sidebar-link ${isActive ? 'active' : ''}`}
                        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    >
                        <Icon className="w-5 h-5" strokeWidth={2} />
                        <span>{item.name}</span>
                    </Link>
                );
            })}
        </nav>
    );

    return (
        <div className="min-h-screen bg-zinc-50">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:bg-white lg:border-r lg:border-zinc-200">
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center h-16 px-6 border-b border-zinc-200">
                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                                <Clock className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-extrabold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                SwissTime
                            </span>
                        </Link>
                    </div>

                    {/* Navigation */}
                    <div className="flex-1 px-4 py-6 overflow-y-auto">
                        <NavLinks />
                    </div>

                    {/* User section */}
                    <div className="p-4 border-t border-zinc-200">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button 
                                    className="flex items-center gap-3 w-full p-2 rounded-sm hover:bg-zinc-100 transition-colors"
                                    data-testid="user-menu-trigger"
                                >
                                    <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                                        <User className="w-4 h-4 text-zinc-600" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                                        <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem disabled>
                                    <User className="w-4 h-4 mr-2" />
                                    Profile
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </aside>

            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-200 h-16">
                <div className="flex items-center justify-between h-full px-4">
                    <Link to="/dashboard" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                            <Clock className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-extrabold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            SwissTime
                        </span>
                    </Link>

                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                <Menu className="w-6 h-6" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-64 p-0">
                            <div className="flex flex-col h-full">
                                <div className="flex items-center justify-between h-16 px-6 border-b border-zinc-200">
                                    <span className="text-xl font-extrabold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        SwissTime
                                    </span>
                                    <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>
                                <div className="flex-1 px-4 py-6 overflow-y-auto">
                                    <NavLinks onItemClick={() => setMobileOpen(false)} />
                                </div>
                                <div className="p-4 border-t border-zinc-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                                            <User className="w-4 h-4 text-zinc-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-zinc-900">{user?.name}</p>
                                            <p className="text-xs text-zinc-500">{user?.email}</p>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        className="w-full" 
                                        onClick={handleLogout}
                                        data-testid="mobile-logout-btn"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Log out
                                    </Button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </header>

            {/* Main Content */}
            <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
                <div className="p-6 md:p-8 lg:p-12">
                    {children}
                </div>
            </main>
        </div>
    );
};
